import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LoginIcon from "@mui/icons-material/Login";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import AccountBoxOutlinedIcon from "@mui/icons-material/AccountBoxOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { getClientWorkspaceApi, getReviewContextApi } from "src/DAL/coachWorkspace/coachWorkspace";
import { member_login_api } from "src/DAL/member/Member1";
import { clientPortalUrl } from "src/config/config";
import { normalizeSubmission } from "src/pages/Trackers/submissionStorage";
import { ActionMenu, Breadcrumbs, CW, ErrorBox, Eyebrow, IconAction, InfoHint, Initials, Loading, PageTitle, Panel, Pill, TextLink, UnderlineTabs } from "./cwUi";
import LatestCheckInPanel from "./LatestCheckInPanel";
import ProgrammeWeekPanel from "./ProgrammeWeekPanel";
import CoachNotebook from "./CoachNotebook";
import ScheduleChangeDialog from "./ScheduleChangeDialog";
import PlanSkipDialog from "./PlanSkipDialog";
import SubmitForClient from "./SubmitForClient";

/**
 * WORKSPACE (3 Nov) — one client's coaching workspace (client video
 * 13:18–15:00: "this page will be replaced by this page"; design PDF page 2
 * without a check-in to review). NEW path /coach/clients/:clientKey
 * (memberId, else e-mail / person key). Replaces, for coaching, the old
 * Member profile (/member/member-profile/:id), which stays and is linked
 * ("Old profile page": edit, forms, weight tracker, points, password).
 * Per journey: check-in day · Change, last / next check-in, the reply still
 * owed, Plan / skip, Submit check-in for client; Latest check-in, Programme
 * (Open calendar) and Coach notebook as on the review page; check-in history.
 * Phone: Check-in / Programme / Notes tabs.
 * ACTIONS-MENU (3 Nov): Chat icon + "⋮" (Login as member, Submit check-in
 * for client, Old profile page); check-in day = day + pencil; "⋮" Plan / skip.
 */

/** Most urgent reply across journeys: overdue first, then the earliest deadline. */
const mostUrgent = (journeys = []) =>
  journeys
    .filter((j) => j.reply)
    .map((j) => ({ ...j.reply, trackerId: j.tracker.id, trackerName: j.tracker.name }))
    .sort((a, b) => Number(Boolean(b.overdue)) - Number(Boolean(a.overdue)) || new Date(a.reviewDueAt || 0) - new Date(b.reviewDueAt || 0))[0] || null;

const stateTone = { needs_review: "teal", feedback_to_send: "teal", feedback_sent: "green", excepted: "green", reviewed: "green", submitted: "muted" };

function StripItem({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Eyebrow sx={{ fontSize: 10.5 }}>{label}</Eyebrow>
      <Box sx={{ fontSize: 14, fontWeight: 600, color: CW.ink, mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

export default function ClientWorkspacePage() {
  const { clientKey } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [journeyId, setJourneyId] = useState("");
  const submitRef = useRef(null);
  const [ctx, setCtx] = useState(null);
  const [ctxError, setCtxError] = useState("");
  const [week, setWeek] = useState(null);
  const [mobileTab, setMobileTab] = useState("checkin");
  const [dayOpen, setDayOpen] = useState(false);
  const [planTab, setPlanTab] = useState(null);

  const load = useCallback(async () => {
    setError("");
    const result = await getClientWorkspaceApi(clientKey);
    if (result?.code === 200) {
      setData(result);
      // First open the journey where a reply is owed (most urgent), else the newest.
      setJourneyId((id) =>
        id && result.journeys.some((j) => j.tracker.id === id)
          ? id
          : mostUrgent(result.journeys)?.trackerId || result.journeys[0]?.tracker.id || ""
      );
    } else setError(result?.message || "Could not load this client");
  }, [clientKey]);

  useEffect(() => {
    setData(null);
    setJourneyId("");
    load();
  }, [load]);

  const journey = useMemo(() => (data?.journeys || []).find((j) => j.tracker.id === journeyId) || null, [data, journeyId]);
  const latestId = journey?.latestSubmissionId || "";

  const loadCtx = useCallback(async () => {
    if (!latestId) return;
    setCtxError("");
    const result = await getReviewContextApi(latestId);
    if (result?.code === 200) setCtx(result);
    else setCtxError(result?.message || "Could not load the latest check-in");
  }, [latestId]);

  useEffect(() => {
    setCtx(null);
    loadCtx();
  }, [loadCtx]);

  const latest = useMemo(() => (ctx ? normalizeSubmission(ctx.submission) : null), [ctx]);

  if (error && !data) return <ErrorBox message={error} onRetry={load} />;
  if (!data) return <Loading />;

  const person = data.person || {};
  const memberId = person.memberId || "";
  const schedule = journey?.schedule || null;
  const reply = journey?.reply || null;
  // Header pill = the whole client (all journeys); the strip = this journey.
  const owed = mostUrgent(data.journeys);
  const reloadAll = () => {
    load();
    loadCtx();
  };

  const loginAsMember = async () => {
    if (!person.email) {
      enqueueSnackbar("This client has no email on file", { variant: "warning" });
      return;
    }
    const result = await member_login_api({ email: person.email, user_type: "client" });
    if (result?.code === 200) window.open(`${clientPortalUrl}login-by-admin/${result.token}`);
    else enqueueSnackbar(result?.message || "Could not log in as member", { variant: "error" });
  };

  const openCalendar = () => {
    if (!memberId) return;
    const programmeId = (week?.tasks || []).find((t) => t.programmeId)?.programmeId;
    if (programmeId) navigate(`/member/${memberId}/member-MemberCalendar/${programmeId}`);
    else navigate(`/member/member-profile/${memberId}`);
  };

  const openSubmission = (s) =>
    navigate(`/coach/review/${s.id}${s.state === "feedback_to_send" ? "/feedback" : ""}`);

  const dayName = String(schedule?.current?.label || "").split("·")[0].trim();
  const owedWhere = owed && (data.journeys || []).length > 1 ? ` · ${owed.trackerName}` : "";
  const statusPill = owed ? (
    <Pill tone={owed.overdue ? "red" : "teal"} sx={{ fontSize: 13.5, px: 2, py: 0.75, cursor: "pointer" }}>
      <Box component="span" onClick={() => setJourneyId(owed.trackerId)} data-testid="client-status-pill">
        {owed.overdue ? `Reply ${String(owed.deadlineLabel || "overdue").replace(/^Overdue/i, "overdue")}` : `${owed.stateLabel} · ${owed.deadlineLabel}`}
        {owedWhere}
      </Box>
    </Pill>
  ) : (
    <Pill tone="green" sx={{ fontSize: 13.5, px: 2, py: 0.75 }}>
      Up to date
    </Pill>
  );
  const col = (key) => ({ display: { xs: mobileTab === key ? "block" : "none", lg: "block" }, minWidth: 0 });

  return (
    <div className="container-fluid">
      <Box sx={{ maxWidth: 1320, mx: "auto", pb: { xs: 10, lg: 4 } }}>
        <Breadcrumbs items={[{ label: "Clients", onClick: () => navigate("/coach/clients") }, { label: person.name || "Client" }]} />

        {/* Client header */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start", justifyContent: "space-between", mt: 1, mb: 2.5 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", minWidth: 0 }}>
            <Initials name={person.name} size={44} />
            <Box sx={{ minWidth: 0 }}>
              <PageTitle>
                {person.name}
                <InfoHint id="workspace.header" />
              </PageTitle>
              <Typography sx={{ fontSize: { xs: 13.5, md: 15 }, color: CW.muted, mt: 0.5 }}>
                {[(data.journeys || []).map((j) => j.tracker.name).join(", "), person.email].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "flex-start", md: "flex-end" }, gap: 1 }}>
            {statusPill}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {memberId ? (
                <IconAction title="Chat" testId="workspace-chat" onClick={() => navigate(`/chat/?chat_id=${memberId}`)}>
                  <ChatBubbleOutlineIcon fontSize="small" />
                </IconAction>
              ) : null}
              <ActionMenu
                testId="workspace-actions"
                label="Client actions"
                items={[
                  { label: "Login as member", icon: <LoginIcon fontSize="small" />, onClick: loginAsMember, disabled: !person.email, testId: "menu-login-member" },
                  {
                    label: "Submit check-in for client",
                    icon: <AssignmentOutlinedIcon fontSize="small" />,
                    onClick: () => submitRef.current && submitRef.current.open(),
                    hidden: !journey,
                    testId: "menu-submit-for-client",
                  },
                  {
                    label: "Old profile page",
                    icon: <AccountBoxOutlinedIcon fontSize="small" />,
                    onClick: () => navigate(`/member/member-profile/${memberId}`),
                    hidden: !memberId,
                    testId: "menu-old-profile",
                  },
                ]}
              />
            </Box>
          </Box>
        </Box>

        {(data.journeys || []).length > 1 ? (
          <UnderlineTabs
            value={journeyId}
            onChange={setJourneyId}
            sx={{ mb: 2 }}
            options={data.journeys.map((j) => ({ value: j.tracker.id, label: `${j.tracker.name}${j.left ? " (left)" : ""}` }))}
          />
        ) : null}

        {journey ? (
          <>
            {/* Info strip */}
            <Panel info="workspace.strip" sx={{ mb: 2.5, py: { xs: 1.5, md: 1.75 } }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr)) auto" },
                  gap: { xs: 1.5, md: 3 },
                  alignItems: "center",
                }}
              >
                <StripItem label="Last check-in">{journey.submissions[0]?.label || "—"}</StripItem>
                <StripItem label="Next check-in">
                  {schedule?.open ? (
                    <Box component="span" sx={{ color: CW.amber }}>
                      Open now · closes {schedule.open.closesLabel || "—"}
                    </Box>
                  ) : (
                    schedule?.next?.opensLabel || "—"
                  )}
                </StripItem>
                <StripItem label="Check-in day">
                  <Box component="span" sx={{ color: CW.primary }}>
                    {dayName || journey.tracker.cadenceLabel || "—"}
                    {schedule?.current?.source === "person" ? " (own day)" : ""}
                  </Box>
                  {schedule?.canChangeDay && ctx ? (
                    <IconAction
                      title="Change check-in day"
                      testId="change-day"
                      onClick={() => setDayOpen(true)}
                      sx={{ ml: 0.75, p: 0.25, border: 0, bgcolor: "transparent", verticalAlign: "middle" }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconAction>
                  ) : null}
                </StripItem>
                <StripItem label="Coach reply">
                  {reply ? (
                    <Box component="span" sx={{ color: reply.overdue ? CW.red : CW.ink }}>
                      {reply.deadlineLabel}
                    </Box>
                  ) : (
                    <Box component="span" sx={{ color: CW.green }}>
                      Nothing owed
                    </Box>
                  )}
                </StripItem>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", justifyContent: { xs: "flex-start", md: "flex-end" }, flexWrap: "wrap", gridColumn: { xs: "1 / -1", md: "auto" } }}>
                  {reply ? (
                    <TextLink onClick={() => openSubmission(reply)} sx={{ fontWeight: 600, fontSize: 14.5 }}>
                      {reply.state === "feedback_to_send" ? "Write feedback" : "Open review"}
                    </TextLink>
                  ) : null}
                  {ctx ? (
                    <ActionMenu
                      testId="workspace-plan-actions"
                      label="Review plan and skip"
                      items={[{ label: "Plan / skip", icon: <EventNoteOutlinedIcon fontSize="small" />, onClick: () => setPlanTab("plan"), testId: "menu-plan-skip" }]}
                    />
                  ) : null}
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: journey.left || (schedule?.upcoming || []).some((u) => u.skipped) ? 1.25 : 0, alignItems: "center" }}>
                <SubmitForClient
                  ref={submitRef}
                  hideTrigger
                  trackerId={journey.tracker.id}
                  person={{ memberId, personKey: journey.personKey, email: person.email, name: person.name }}
                  onSaved={reloadAll}
                />
                {journey.left ? <Pill tone="muted">Left this journey</Pill> : null}
                {(schedule?.upcoming || []).some((u) => u.skipped) ? (
                  <Typography sx={{ fontSize: 12.5, color: CW.amber }}>
                    Skipped: {(schedule.upcoming || []).filter((u) => u.skipped).map((u) => u.opensLabel).join(", ")}
                  </Typography>
                ) : null}
              </Box>
            </Panel>

            <UnderlineTabs
              value={mobileTab}
              onChange={setMobileTab}
              sx={{ display: { xs: "flex", lg: "none" }, mb: 2 }}
              options={[
                { value: "checkin", label: "Check-in" },
                { value: "programme", label: "Programme" },
                { value: "notes", label: "Notes" },
              ]}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1.15fr) minmax(0, 0.95fr)" },
                gap: 2.5,
                alignItems: "stretch",
                mb: 2.5,
              }}
            >
              <Box sx={col("checkin")}>
                {latest ? (
                  <LatestCheckInPanel
                    submission={latest}
                    history={ctx.history}
                    subtitle={`${journey.submissions[0]?.label || ""} · compared with the previous submission`}
                    info="workspace.latest"
                    onOpenFull={() => navigate(`/tracker-submissions/${latest.id}`)}
                    sx={{ height: "100%", display: "flex", flexDirection: "column" }}
                  />
                ) : (
                  <Panel title="Latest check-in" info="workspace.latest" sx={{ height: "100%" }}>
                    {ctxError ? <Typography sx={{ color: CW.red, fontSize: 13.5 }}>{ctxError}</Typography> : <Loading />}
                  </Panel>
                )}
              </Box>
              <Box sx={col("programme")}>
                <ProgrammeWeekPanel
                  memberId={memberId}
                  onWeekLoaded={setWeek}
                  onOpenCalendar={memberId ? openCalendar : undefined}
                  clientFirstName={String(person.name || "").split(" ")[0]}
                  sx={{ height: "100%" }}
                />
              </Box>
              <Box sx={col("notes")}>
                <CoachNotebook
                  key={latestId}
                  memberId={memberId}
                  submissionId={latestId}
                  onOpenFeedback={reply ? () => navigate(`/coach/review/${reply.id}/feedback`) : undefined}
                  sx={{ height: "100%" }}
                />
              </Box>
            </Box>

            <Panel title="Check-in history" info="workspace.history" action={<Pill tone="teal">{journey.checkIns} total</Pill>}>
              <Box sx={{ display: { xs: "none", md: "grid" }, gridTemplateColumns: "minmax(0,1.2fr) minmax(0,1fr) minmax(0,1fr) 140px", gap: 2, py: 1 }}>
                {["Submitted", "Form", "Review", "Action"].map((h) => (
                  <Eyebrow key={h}>{h}</Eyebrow>
                ))}
              </Box>
              {journey.submissions.map((s) => (
                <Box
                  key={s.id}
                  data-testid="history-row"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr auto", md: "minmax(0,1.2fr) minmax(0,1fr) minmax(0,1fr) 140px" },
                    gap: { xs: 0.5, md: 2 },
                    alignItems: "center",
                    py: 1.1,
                    borderTop: `1px solid ${CW.line}`,
                  }}
                >
                  <Typography sx={{ fontSize: 13.5, color: CW.ink, fontWeight: 600 }} noWrap>
                    {s.label}
                    {s.late ? (
                      <Pill tone="amber" sx={{ ml: 1, fontSize: 10.5, px: 0.9, py: 0.1 }}>
                        Late
                      </Pill>
                    ) : null}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: CW.muted, display: { xs: "none", md: "block" } }} noWrap>
                    {s.formName || "—"}
                  </Typography>
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <Pill tone={s.overdue ? "red" : stateTone[s.state] || "muted"}>
                      {s.stateLabel}
                      {s.deadlineLabel ? ` · ${s.deadlineLabel}` : ""}
                    </Pill>
                  </Box>
                  <TextLink onClick={() => openSubmission(s)} sx={{ fontSize: 13.5 }}>
                    {s.state === "feedback_to_send" ? "Write feedback" : s.state === "needs_review" ? "Open review" : "View"}
                  </TextLink>
                </Box>
              ))}
              <Typography sx={{ fontSize: 12.5, color: CW.muted, mt: 1.5 }}>
                Last 10 check-ins on this journey. Older ones: View full answers and history.
              </Typography>
            </Panel>
          </>
        ) : null}
      </Box>

      {journey && schedule && ctx ? (
        <ScheduleChangeDialog
          open={dayOpen}
          onClose={() => setDayOpen(false)}
          trackerId={journey.tracker.id}
          personKey={ctx.person?.personKey || journey.personKey}
          memberId={memberId}
          personName={person.name}
          trackerName={journey.tracker.name}
          current={ctx.schedule?.current || schedule.current}
          rules={ctx.schedule?.rules || schedule.rules}
          onSaved={reloadAll}
        />
      ) : null}
      {journey && ctx ? (
        <PlanSkipDialog
          open={Boolean(planTab)}
          initialTab={planTab || "plan"}
          onClose={() => setPlanTab(null)}
          submissionId={latestId}
          trackerId={journey.tracker.id}
          personKey={ctx.person?.personKey || journey.personKey}
          memberId={memberId}
          personName={person.name}
          reply={ctx.reply}
          schedule={ctx.schedule}
          onChangeDay={ctx.schedule?.canChangeDay ? () => { setPlanTab(null); setDayOpen(true); } : undefined}
          onSaved={reloadAll}
        />
      ) : null}
    </div>
  );
}
